import { useState, useSyncExternalStore } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp } from '../../icons';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { MonthYearPicker } from './MonthYearPicker';

/**
 * 헤더 타이틀 팝오버의 **주간/일간용** 선택기 (2026-09-14 사용자 요청).
 *
 * 월간은 연/월만 고르면 되므로 기존 MonthYearPicker 를 그대로 쓴다. 주간·일간은 달을 골라 봐야
 * 그 달 1일로만 갈 수 있어 원하는 주·날짜까지 다시 스크롤해야 했다 — 그래서 여기서는 달력을
 * 펼쳐 보여 주고, 주간이면 **주 한 줄**을, 일간이면 **하루**를 직접 고르게 한다.
 *
 * 고른 값은 «누른 그 날짜»로 onPick 에 넘긴다 — 화면 이동(커서·스크롤)은 부모가 뷰에 맞게 처리한다.
 * ⭐ 2026-10-30 (사용자 승인) — 주간도 그 날이다. 종전에는 그 줄의 일요일을 넘겨 10/29(목)를 눌러도
 *   선택 표시가 10/25(일)에 붙었다. 「주를 고른다」는 느낌(줄 호버 띠 · 지금 주 줄 띠)은 그대로다.
 */
export type HeaderPickMode = 'week' | 'day';

interface HeaderDatePickerProps {
  mode: HeaderPickMode;
  /** 지금 보고 있는 기준 날짜 — 처음 열 때 이 달을 펼치고, 이 주/날짜에 선택 표시를 한다. */
  value: Date;
  onPick: (date: Date) => void;
  /**
   * 주간 — 줄 띠 안에서 따로 표시할 «선택한 날»(2026-10-30). 안 주면 `value` 의 날.
   * 일간은 쓰지 않는다(종전대로 `value` 가 선택일이다).
   */
  selected?: Date;
}

const DOW_KEYS = [
  'calendar.month.dayname.sun',
  'calendar.month.dayname.mon',
  'calendar.month.dayname.tue',
  'calendar.month.dayname.wed',
  'calendar.month.dayname.thu',
  'calendar.month.dayname.fri',
  'calendar.month.dayname.sat',
] as const;

function startOfDay(d: Date): Date {
  const c = new Date(d); c.setHours(0, 0, 0, 0); return c;
}
function weekStartOf(d: Date): Date {
  const c = startOfDay(d); c.setDate(c.getDate() - c.getDay()); return c;
}
function sameDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

export function HeaderDatePicker({ mode, value, onPick, selected }: HeaderDatePickerProps) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);

  // 연도 → 달 → (주|날짜) 순으로 좁혀 간다 (2026-09-14 사용자 지시). 앞의 두 단계는 월간이
  // 쓰는 드릴다운(MonthYearPicker)을 그대로 재사용한다 — 같은 자리에서 모양이 달라지지 않는다.
  // 팝오버는 열릴 때마다 새로 마운트되므로(조건부 렌더) 항상 연도 단계에서 시작한다.
  const [picked, setPicked] = useState<{ year: number; month: number } | null>(null);

  if (picked == null) {
    return (
      <MonthYearPicker
        year={value.getFullYear()}
        month={value.getMonth()}
        onPick={(year, month) => setPicked({ year, month })}
      />
    );
  }

  const viewMonth = new Date(picked.year, picked.month, 1);
  const setViewMonth = (next: Date) => setPicked({ year: next.getFullYear(), month: next.getMonth() });

  const monthIdx = viewMonth.getMonth();
  const gridStart = weekStartOf(viewMonth);
  // 6주 × 7일 — 본체 월 그리드·미니 캘린더와 같은 기준(일요일 시작).
  const weeks = Array.from({ length: 6 }, (_, w) => (
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + w * 7 + i);
      return d;
    })
  ));

  const today = startOfDay(new Date());
  const selectedWeek = weekStartOf(value).getTime();
  const selectedDay = selected ?? value;

  function shiftMonth(delta: number) {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + delta, 1));
  }

  function dayClass(d: Date): string {
    return [
      'cal-dp-day',
      d.getMonth() !== monthIdx ? 'cal-dp-day--other' : '',
      d.getDay() === 0 ? 'cal-dp-day--sun' : '',
      d.getDay() === 6 ? 'cal-dp-day--sat' : '',
      sameDay(d, today) ? 'is-today' : '',
      mode === 'day' && sameDay(d, value) ? 'is-sel' : '',
    ].filter(Boolean).join(' ');
  }

  return (
    <div className="cal-dp">
      <div className="cal-dp-head">
        <button
          type="button"
          className="cal-dp-navbtn"
          aria-label={tCalendar('calendar.nav.prev_month')}
          onClick={() => shiftMonth(-1)}
        >
          <ChevronLeft size={15} />
        </button>
        {/* 제목을 누르면 연도 단계로 되돌아간다(월간 피커의 "YYYY년 ▲" 와 같은 약속). */}
        <button type="button" className="cal-dp-title" onClick={() => setPicked(null)}>
          {tCalendar('calendar.picker.month_title', {
            year: viewMonth.getFullYear(), month: viewMonth.getMonth() + 1,
          })}
          <ChevronUp size={13} aria-hidden />
        </button>
        <button
          type="button"
          className="cal-dp-navbtn"
          aria-label={tCalendar('calendar.nav.next_month')}
          onClick={() => shiftMonth(1)}
        >
          <ChevronRight size={15} />
        </button>
      </div>

      <div className="cal-dp-dow">
        {DOW_KEYS.map((k, i) => (
          <span
            key={k}
            className={`cal-dp-dowcell${i === 0 ? ' cal-dp-day--sun' : ''}${i === 6 ? ' cal-dp-day--sat' : ''}`}
          >
            {tCalendar(k)}
          </span>
        ))}
      </div>

      <div className="cal-dp-grid">
        {weeks.map((week) => {
          const weekStart = week[0]!;
          // 주간 — 줄은 «주»를 보여 주는 띠(호버 · 지금 주)이고, 날짜마다 단추라 누른 «그 날»이 넘어간다
          //   (2026-10-30 · 종전: 줄 전체가 단추 하나 → 늘 일요일). 화면은 그 날이 든 주로 간다.
          if (mode === 'week') {
            const isSel = weekStart.getTime() === selectedWeek;
            return (
              <div
                key={weekStart.getTime()}
                className={`cal-dp-week cal-dp-week--pickweek${isSel ? ' is-sel' : ''}`}
                role="group"
                aria-label={tCalendar('calendar.picker.week_of', {
                  month: weekStart.getMonth() + 1, day: weekStart.getDate(),
                })}
              >
                {week.map((d) => {
                  const isDay = sameDay(d, selectedDay);
                  return (
                    <button
                      key={d.getTime()}
                      type="button"
                      className={`${dayClass(d)}${isDay ? ' is-sel' : ''}`}
                      aria-pressed={isDay}
                      aria-label={tCalendar('calendar.picker.day_label', {
                        year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(),
                      })}
                      onClick={() => onPick(startOfDay(d))}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            );
          }
          return (
            <div className="cal-dp-week cal-dp-week--days" key={weekStart.getTime()} role="row">
              {week.map((d) => (
                <button
                  key={d.getTime()}
                  type="button"
                  className={dayClass(d)}
                  aria-pressed={sameDay(d, value)}
                  onClick={() => onPick(startOfDay(d))}
                >
                  {d.getDate()}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
